import { Component, Inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { rubricEditorModalToken } from 'go-modules/modals/rubric-editor/modal.factory';
import { mediaPreviewToken } from 'go-modules/modals/media-preview/modal.factory';
import { mediaToken } from 'go-modules/models/media/media.factory';
import { PreviewMarkerSetDialogComponent } from '../dialogs/preview-marker-set-dialog/preview-marker-set-dialog.component';
import { ActivityInstructionModel } from 'go-modules/models/activity';
import { LibraryService } from '@go-modules/services/library/library.service';
import type { Marker, MarkerSet } from '@go-modules/interfaces/markers/marker';
import type { Session } from '@go-modules/services/session-list-datasource/session-list.datasource';
import { ActivityTemplateModelType } from 'go-modules/models/activity-template/activity-template.factory';
import type { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { BehaviorSubject } from 'rxjs';

export enum ViewFrom {
	ACTIVITY_INSTRUCTIONS = 'Activity instructions',
	ACTIVITY_OVERVIEW = 'Activity overview'
}

@Component({
	selector: 'activity-info',
	templateUrl: './activity-info.component.html',
	styleUrls: ['./activity-info.component.scss'],
	standalone: false
})
export class ActivityInfoComponent implements OnInit, OnChanges {
	@Input() public activity: ActivityModel;
	@Input() public viewFrom = ViewFrom.ACTIVITY_INSTRUCTIONS;
	@Input() public hideSourceMedia: boolean;
	@Input() public hideRecordingInstructions: boolean;
	@Input() public hideNumOfAttempts: boolean = false;
	@Input() public hideTimeLimit: boolean = false;
	@Input() public hidePoints: boolean = false;
	@Input() public hideInstructions: boolean = false;
	@Input() public isOnDashboard: boolean;
	@Input() public session: Session;

	public hasMarkerSet$ = new BehaviorSubject(false);
	public markerSet: MarkerSet;
	public aiMarkers: Marker[];
	public readonly viewFromEnum = ViewFrom;

	constructor (
		private translate: TranslateService,
		private dialog: MatDialog,
		private libraryService: LibraryService,
		@Inject(mediaToken) public MediaModel,
		@Inject(rubricEditorModalToken) private rubricEditorModal,
		@Inject(mediaPreviewToken) private mediaPreviewModal
	) {}

	public ngOnInit () {
		this.determineSelectedMarkerSet();

		if (this.activity.llm_definitions) {
			this.aiMarkers = this.activity.llm_definitions.map((definition) => {
				return definition.tags[0]; // first tag from each definition
			}) as Marker[];
		}
	}

	public ngOnChanges (changes: SimpleChanges): void {
		if (changes.activity) {
			this.determineSelectedMarkerSet();
		}
	}

	public somethingToShow (): boolean {
		return this.showSourceMedia()
			|| this.showInstructions(ActivityInstructionModel.TYPE.RECORDING)
			|| this.showInstructions(ActivityInstructionModel.TYPE.FEEDBACK)
			|| this.showAttachments();
	}

	public showSourceMedia (): boolean {
		return this.activity.source_media &&
			this.activity.source_media.media_id > 0 &&
			!this.hideSourceMedia;
	}

	public showInstructions (type: string): boolean {
		if (this.hideInstructions) {
			return false;
		}

		if (type === ActivityInstructionModel.TYPE.RECORDING) {
			return this.activity.hasRecordingInstructions() && !this.hideRecordingInstructions;
		} else if (type === ActivityInstructionModel.TYPE.FEEDBACK) {
			return this.activity.hasFeedbackInstructions() &&
				(this.session?.isRecorded() || this.activity.isCommentOnly());
		}
	}

	public showAttachments (): boolean {
		return this.activity.attachments.length > 0;
	}

	public shouldShowActivityPicture (): boolean {
		return !this.activity.isConversationBoard();
	}

	public showActivityPicture (): void {
		const media = this.MediaModel.model({
			media_status: this.MediaModel.READY,
			media_type: this.MediaModel.TYPE.DOCUMENT,
			filename: 'fake_name.jpg'
		});
		switch (this.activity.template.type) {
			case ActivityTemplateModelType.STIMULUS_RESPONSE:
				media.media_url = 'https://staticassets.goreact.com/stimulus.png';
				media.title = this.translate.instant('activity-info_example-stimulus');
				break;
			case ActivityTemplateModelType.FEEDBACK_ONLY:
				media.media_url = 'https://staticassets.goreact.com/commentonly.png';
				media.title = this.translate.instant('activity-info_example-comment-only');
				break;
			default:
				media.media_url = 'https://staticassets.goreact.com/standard.png';
				media.title = this.translate.instant('activity-info_example-standard');
		}

		const modalData = {
			media,
			edit: false,
			allowDownload: false
		};

		this.mediaPreviewModal.open({
			modalData
		});
	}

	public getActivityTypeName (): string {
		if (this.activity.is_conversation && this.activity.has_response_media) {
			return this.activity.live_session_enabled
				? this.translate.instant('common_standard-group-with-live-review')
				: this.translate.instant('common_standard-group');
		} else if (this.activity.live_session_enabled) {
			if (this.activity.template.slug === ActivityTemplateModelType.STIMULUS_RESPONSE) {
				// Live stimulus
				return this.translate.instant('common_stimulus-with-live-review');
			}
			return this.translate.instant('common_standard-with-live-review');
		} else {
			return this.activity.template?.type;
		}
	}

	public isRubricEnabled (): boolean {
		return this.activity.isRubricEnabled() || this.activity.isPeerRubricEnabled();
	}

	public previewRubric (): void {
		const modalData = {
			options: {
				schemaId: this.activity.rubric_template_id || this.activity.peer_rubric_id,
				mode: 'print',
				previewOnly: true
			}
		};

		this.rubricEditorModal.open({
			modalData
		});
	}

	public previewMarkerSet (): void {
		this.dialog.open(PreviewMarkerSetDialogComponent, {data: {
			markerSet: this.markerSet,
			withName: true
		}});
	}

	public previewAiMarkers (): void {
		this.dialog.open(PreviewMarkerSetDialogComponent, {data: {
			markerSet: {
				tags: this.aiMarkers
			},
			aiStyle: true,
			withName: true
		}});
	}

	public shouldSeePreviewAiMarkers (): boolean {
		return this.activity.llm_definitions && this.activity.llm_definitions.length > 0;
	}

	public getSourceMediaLabel (): string {
		if (this.activity.template.type === ActivityTemplateModelType.STIMULUS_RESPONSE) {
			return 'assignment-information-stimulus-title';
		}

		return 'activity-editor-source-media-panel-header';
	}

	private determineSelectedMarkerSet (): void {
		if (this.activity.tag_set_id > 0) {
			this.libraryService.getMarkerSet(this.activity.tag_set_id).subscribe((markerSet) => {
				this.markerSet = markerSet;
				this.hasMarkerSet$.next(true);
			});
		} else {
			this.markerSet = null;
			this.hasMarkerSet$.next(false);
		}
	}
}
